import { ArrowRightOutlined } from "@ant-design/icons";
import { GamificationAchievement, GamificationAchievementFilter, SDK } from "@axl-dev/school-sdk";
import { Modal, Spin, theme } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useGamificationBalance } from "@app/shared/lib/GamificationBalanceProvider";
import { useLoc } from "@app/shared/lib/hooks";
import { useUser } from "@app/shared/lib/hooks/useUser";
import { formatDate } from "@app/shared/lib/date";
import { GamificationTransactionTable } from "@app/pages/gamification/components/GamificationTransactionTable/GamificationTransactionTable";
import { UserAvatar } from "@app/shared/ui/UserAvatar";
import { Image } from "@app/shared/ui";
import { generateBtnHoverCss, parseBtnHoverProps, BtnHoverProps, sanitizeCssValue as sc } from "@app/widgets/shared/btnHoverCss";
import { deviceVarCss } from "@app/widgets/shared/deviceCss";

type BlockType = "points" | "achievements";

export type GamificationWidgetProps = BtnHoverProps & {
  sdk: SDK;
  blockType: BlockType;
  cardBgColor: string;
  cardBorderColor: string;
  cardBorderRadius: string;
  cardPadding: string;
  titleFontSize: string;
  titleFontFamily: string;
  titleFontWeight: string;
  titleTextColor: string;
  titleTextAlign: string;
  pointsAvatarSize: string;
  pointsNameFontSize: string;
  pointsNameColor: string;
  pointsValueFontSize: string;
  pointsValueFontSizeTablet: string;
  pointsValueFontSizeMobile: string;
  pointsValueColor: string;
  pointsLabelFontSize: string;
  pointsBoxBgColor: string;
  pointsLabelColor: string;
  showTransactionHistory: boolean;
  achievementImageSize: string;
  achievementTitleFontSize: string;
  achievementTitleFontSizeTablet: string;
  achievementTitleFontSizeMobile: string;
  achievementTitleColor: string;
  achievementDescFontSize: string;
  achievementDescColor: string;
  achievementPointsColor: string;
  achievementPointsFontSize: string;
  achievementDateColor: string;
  achievementDateFontSize: string;
  showAchievementDate: boolean;
  showAchievementDesc: boolean;
  showAchievementPoints: boolean;
  achievementColumns: number;
  showAllAchievementsBtn: boolean;
  emptyText: string;
  emptyTextColor: string;
  emptyTextFontSize: string;
  btnText: string;
  btnBgColor: string;
  btnTextColor: string;
  btnBorderRadius: string;
  btnFontSize: string;
  title?: string;
  prefetchedTotals?: any[];
  prefetchedAchievements?: GamificationAchievement[];
};

export const parseGamificationProps = (container: HTMLElement): Omit<GamificationWidgetProps, "sdk"> => {
  const attr = (name: string, fallback = "") => container.getAttribute(name) || fallback;
  return {
    blockType: attr("data-block-type", "points") as BlockType,
    cardBgColor: attr("data-card-bg-color", "#ffffff"),
    cardBorderColor: attr("data-card-border-color", "#f0f0f0"),
    cardBorderRadius: attr("data-card-border-radius", "8px"),
    cardPadding: attr("data-card-padding", "20px"),
    titleFontSize: attr("data-title-font-size", "20px"),
    titleFontFamily: attr("data-title-font-family"),
    titleFontWeight: attr("data-title-font-weight", "600"),
    titleTextColor: attr("data-title-text-color"),
    titleTextAlign: attr("data-title-text-align", "left"),
    pointsAvatarSize: attr("data-points-avatar-size", "85px"),
    pointsNameFontSize: attr("data-points-name-font-size", "24px"),
    pointsNameColor: attr("data-points-name-color", "#333333"),
    pointsValueFontSize: attr("data-points-value-font-size", "32px"),
    pointsValueFontSizeTablet: attr("data-points-value-font-size-tablet", ""),
    pointsValueFontSizeMobile: attr("data-points-value-font-size-mobile", ""),
    pointsValueColor: attr("data-points-value-color", "#1890ff"),
    pointsLabelFontSize: attr("data-points-label-font-size", "14px"),
    pointsBoxBgColor: attr("data-points-box-bg-color", "#f5f5f5"),
    pointsLabelColor: attr("data-points-label-color", "#999999"),
    showTransactionHistory: attr("data-show-transaction-history", "true") === "true",
    achievementImageSize: attr("data-achievement-image-size", "64px"),
    achievementTitleFontSize: attr("data-achievement-title-font-size", "14px"),
    achievementTitleFontSizeTablet: attr("data-achievement-title-font-size-tablet", ""),
    achievementTitleFontSizeMobile: attr("data-achievement-title-font-size-mobile", ""),
    achievementTitleColor: attr("data-achievement-title-color", "#333333"),
    achievementDescFontSize: attr("data-achievement-desc-font-size", "12px"),
    achievementDescColor: attr("data-achievement-desc-color", "#888888"),
    achievementPointsColor: attr("data-achievement-points-color", "#52c41a"),
    achievementPointsFontSize: attr("data-achievement-points-font-size", "24px"),
    achievementDateColor: attr("data-achievement-date-color", "#999999"),
    achievementDateFontSize: attr("data-achievement-date-font-size", "12px"),
    showAchievementDate: attr("data-show-achievement-date", "true") === "true",
    showAchievementDesc: attr("data-show-achievement-desc", "true") === "true",
    showAchievementPoints: attr("data-show-achievement-points", "true") === "true",
    achievementColumns: parseInt(attr("data-achievement-columns", "1")) || 1,
    showAllAchievementsBtn: attr("data-show-all-achievements-btn", "true") === "true",
    emptyText: attr("data-empty-text"),
    emptyTextColor: attr("data-empty-text-color", "#aaaaaa"),
    emptyTextFontSize: attr("data-empty-text-font-size", "14px"),
    btnText: attr("data-btn-text"),
    btnBgColor: attr("data-btn-bg-color", "#1890ff"),
    btnTextColor: attr("data-btn-text-color", "#ffffff"),
    btnBorderRadius: attr("data-btn-border-radius", "5px"),
    btnFontSize: attr("data-btn-font-size", "14px"),
    title: attr("data-title") || undefined,
    ...parseBtnHoverProps(container),
  };
};

const HeaderLink: React.FC<{ text: string; onClick: () => void; color?: string; btnBgColor?: string; btnTextColor?: string }> = ({ text, onClick, color, btnBgColor, btnTextColor }) => {
  const { token } = theme.useToken();
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
      <a
        className="ez-cab-btn ez-gamification-block-btn"
        onClick={(e) => { e.preventDefault(); onClick(); }}
        href="#"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          fontWeight: 500,
          color: btnTextColor || color || (btnBgColor ? token.colorTextLightSolid : token.colorPrimary),
          background: btnBgColor || "transparent",
          padding: btnBgColor ? "6px 14px" : "0",
          textDecoration: "none",
          cursor: "pointer",
        }}
      >
        {text} <ArrowRightOutlined style={{ fontSize: "12px" }} />
      </a>
    </div>
  );
};

const PointsBlock: React.FC<GamificationWidgetProps> = (props) => {
  const { user } = useUser();
  const balance = useGamificationBalance();
  const loc = useLoc();
  const { token } = theme.useToken();
  const [totals, setTotals] = useState<any[]>(props.prefetchedTotals || []);
  const [loading, setLoading] = useState(!props.prefetchedTotals);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (props.prefetchedTotals) { setTotals(props.prefetchedTotals); setLoading(false); return; }
    let cancelled = false;
    props.sdk.api.gamification.fetchTotals().then((res: any) => {
      if (!cancelled && res.success && res.body?.items) setTotals(res.body.items);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [props.sdk, props.prefetchedTotals]);

  const totalEarned = totals.find(x => x.type === "gamificationTotalPoints")?.value ?? 0;

  if (loading) return <Spin />;

  return (
    <>
      {props.showTransactionHistory && (
        <HeaderLink
          text={loc.word("Gamification.transaction.showHistory", { default: "Show history" })}
          onClick={() => setShowHistory(true)}
          color={props.pointsValueColor}
        />
      )}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        {user && <UserAvatar user={user} size={parseInt(props.pointsAvatarSize) || 85} />}
        <div style={{ fontSize: props.pointsNameFontSize || "24px", fontWeight: 600, color: props.pointsNameColor || token.colorText, maxWidth: "100%", textAlign: "center", wordBreak: "break-word" }}>{user?.fullName}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", width: "100%", maxWidth: "100%", marginTop: "10px", boxSizing: "border-box" }}>
          <div style={{ flex: "1 1 100px", minWidth: 0, textAlign: "center", padding: "12px", borderRadius: "8px", backgroundColor: props.pointsBoxBgColor || token.colorFillSecondary, boxSizing: "border-box", overflow: "hidden" }}>
            <div className="ez-gamification-block-points" style={{ fontSize: "var(--gw-points-fs, 32px)", fontWeight: 700, color: props.pointsValueColor || token.colorPrimary }}>{balance ?? 0}</div>
            <div style={{ fontSize: props.pointsLabelFontSize || "14px", color: props.pointsLabelColor || token.colorTextSecondary }}>{loc.word("Gamification.balance", { default: "Balance" })}</div>
          </div>
          <div style={{ flex: "1 1 100px", minWidth: 0, textAlign: "center", padding: "12px", borderRadius: "8px", backgroundColor: props.pointsBoxBgColor || token.colorFillSecondary, boxSizing: "border-box", overflow: "hidden" }}>
            <div className="ez-gamification-block-points" style={{ fontSize: "var(--gw-points-fs, 32px)", fontWeight: 700, color: props.pointsValueColor || token.colorPrimary }}>{totalEarned}</div>
            <div style={{ fontSize: props.pointsLabelFontSize || "14px", color: props.pointsLabelColor || token.colorTextSecondary }}>{loc.word("Gamification.totalEarned", { default: "Total earned" })}</div>
          </div>
        </div>
      </div>
      <Modal
        open={showHistory}
        title={loc.word("Gamification.transaction.history", { default: "Transaction history" })}
        okButtonProps={{ style: { display: "none" } }}
        onCancel={() => setShowHistory(false)}
        width={900}
        destroyOnHidden
      >
        <GamificationTransactionTable />
      </Modal>
    </>
  );
};

const AchievementsBlock: React.FC<GamificationWidgetProps> = (props) => {
  const loc = useLoc();
  const { token } = theme.useToken();
  const [items, setItems] = useState<GamificationAchievement[]>(props.prefetchedAchievements || []);
  const [loading, setLoading] = useState(!props.prefetchedAchievements);
  const [showAll, setShowAll] = useState(false);
  const [allItems, setAllItems] = useState<GamificationAchievement[]>([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const scopeId = useMemo(() => `ez-gwa-${Math.random().toString(36).slice(2, 8)}`, []);
  const deviceCss = useMemo(() => deviceVarCss(`.${scopeId}`, [
    { name: "gw-ach-title-fs", base: props.achievementTitleFontSize || "14px", tablet: props.achievementTitleFontSizeTablet, mobile: props.achievementTitleFontSizeMobile },
  ]), [scopeId, props.achievementTitleFontSize, props.achievementTitleFontSizeTablet, props.achievementTitleFontSizeMobile]);

  useEffect(() => {
    if (props.prefetchedAchievements) { setItems(props.prefetchedAchievements); setLoading(false); return; }
    let cancelled = false;
    props.sdk.api.gamification.fetchAchievements(new GamificationAchievementFilter({ take: 50 })).then((res: any) => {
      if (!cancelled && res.success && res.body?.items) setItems(res.body.items);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [props.sdk, props.prefetchedAchievements]);

  const handleShowAll = useCallback(async () => {
    setShowAll(true);
    setLoadingAll(true);
    try {
      const res = await props.sdk.api.gamification.fetchAchievements(new GamificationAchievementFilter({ useBaseFilter: false, showAll: true } as any));
      if (res.success && res.body?.items) setAllItems(res.body.items);
    } finally { setLoadingAll(false); }
  }, [props.sdk]);


  if (loading) return <Spin />;

  const cols = props.achievementColumns || 1;
  const imgSize = parseInt(props.achievementImageSize) || 64;
  const renderItem = (a: GamificationAchievement) => (
    <div key={a.id} className="ez-cab-card ez-gamification-block-card" style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
      <div style={{ position: "relative", flexShrink: 0 }}>
        <Image cloudKey={a.imageCloudKey} width={imgSize} height={imgSize} fit="fill" className="br-xxs" resize={{ w: imgSize, h: imgSize }} />
        {a.count > 1 && <div style={{ position: "absolute", top: -5, right: -5, width: 20, height: 20, borderRadius: "50%", backgroundColor: token.colorBgElevated, border: `1px solid ${token.colorPrimary}`, fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 500 }}>x{a.count}</div>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="ez-cab-title ez-gamification-block-title" style={{ fontSize: "var(--gw-ach-title-fs, 14px)", fontWeight: 600, color: props.achievementTitleColor || token.colorText }}>{a.title || a.subtype}</div>
        {props.showAchievementDesc && a.description && <div style={{ fontSize: props.achievementDescFontSize || "12px", color: props.achievementDescColor || token.colorTextSecondary, lineHeight: 1.4 }}>{a.description}</div>}
        {props.showAchievementDate && a.achievedDate && <div style={{ fontSize: props.achievementDateFontSize || "12px", color: props.achievementDateColor || token.colorTextTertiary }}>{formatDate(a.achievedDate)}</div>}
      </div>
      {props.showAchievementPoints && <div style={{ fontSize: props.achievementPointsFontSize || "24px", fontWeight: 700, color: props.achievementPointsColor || token.colorSuccess, flexShrink: 0 }}>+{a.points}</div>}
    </div>
  );

  const isEmpty = items.length === 0;

  return (
    <div className={scopeId}>
      <style>{deviceCss}</style>
      {props.showAllAchievementsBtn && (
        <HeaderLink
          text={props.btnText || loc.word("Gamification.achievements.showAll", { default: "Show all achievements" })}
          onClick={handleShowAll}
          btnBgColor={props.btnBgColor}
          btnTextColor={props.btnTextColor}
        />
      )}
      <div style={{ display: "grid", gridTemplateColumns: cols === 1 ? "1fr" : `repeat(auto-fill, minmax(min(100%, calc((100% - ${(cols - 1) * 12}px) / ${cols})), 1fr))`, gap: "12px", width: "100%" }}>
        {isEmpty
          ? <div style={{ width: "100%", textAlign: "center", padding: "24px", color: props.emptyTextColor || token.colorTextTertiary, fontSize: props.emptyTextFontSize || "14px" }}>{props.emptyText || loc.word("Gamification.achievements.empty", { default: "No achievements yet" })}</div>
          : items.map(renderItem)}
      </div>
      <Modal open={showAll} wrapClassName={scopeId} title={loc.word("Gamification.achievements.all", { default: "All achievements" })} footer={null} onCancel={() => setShowAll(false)} width={800} destroyOnHidden>
        {loadingAll ? <Spin /> : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))", gap: "12px" }}>{allItems.map(renderItem)}</div>}
      </Modal>
    </div>
  );
};

export const GamificationWidget: React.FC<GamificationWidgetProps> = (props) => {
  const { title, blockType, cardBgColor, cardBorderColor, cardBorderRadius, cardPadding } = props;
  const { token } = theme.useToken();

  const scopeId = useMemo(() => `ez-gw-${Math.random().toString(36).slice(2, 8)}`, []);
  const hoverCss = useMemo(() => generateBtnHoverCss(`.${scopeId}`, "button, a", props), [scopeId, props.btnHoverEffect, props.btnHoverBgColor, props.btnHoverTextColor, props.btnHoverShadow, props.btnHoverScale, props.btnHoverTransitionDuration]);
  const themeCss = useMemo(() => `.${scopeId} .ez-gamification-block-panel{border:1px solid ${sc(cardBorderColor || token.colorBorder)};border-radius:${sc(cardBorderRadius || "8px")};background-color:${sc(cardBgColor || token.colorBgElevated)};padding:${sc(cardPadding || "20px")};}` + `.${scopeId} .ez-gamification-block-btn{border-radius:${sc(props.btnBorderRadius || "5px")};font-size:${sc(props.btnFontSize || "14px")};}`, [scopeId, cardBorderColor, cardBorderRadius, cardBgColor, cardPadding, props.btnBorderRadius, props.btnFontSize, token]);
  const deviceCss = useMemo(() => deviceVarCss(`.${scopeId}`, [
    { name: "gw-points-fs", base: props.pointsValueFontSize || "32px", tablet: props.pointsValueFontSizeTablet, mobile: props.pointsValueFontSizeMobile },
  ]), [scopeId, props.pointsValueFontSize, props.pointsValueFontSizeTablet, props.pointsValueFontSizeMobile]);

  const cardStyle: React.CSSProperties = {
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.03)",
  };

  return (
    <div className={scopeId}>
      <style>{hoverCss}{deviceCss}{themeCss}</style>
      {title && (
        <div style={{
          fontSize: props.titleFontSize || "20px",
          fontWeight: (props.titleFontWeight as any) || 600,
          fontFamily: props.titleFontFamily || undefined,
          color: props.titleTextColor || undefined,
          textAlign: (props.titleTextAlign as any) || "left",
          marginBottom: "12px",
        }}>{title}</div>
      )}
      <div className="ez-gamification-block-panel" style={cardStyle}>
        {blockType === "points" && <PointsBlock {...props} />}
        {blockType === "achievements" && <AchievementsBlock {...props} />}
      </div>
    </div>
  );
};
